'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Hotspot {
  id: number;
  x: number;
  y: number;
  title: string;
  specs: string[];
  description: string;
}

const hotspots: Hotspot[] = [
{
  id: 1,
  x: 30,
  y: 25,
  title: 'Turret Tool Station',
  description: 'Multi-tool turret with automatic tool selection and quick-change capability for versatile punching operations.',
  specs: [
  '58-Station Tool Turret',
  'Automatic Tool Selection',
  'Quick-Change Tooling',
  'Multi-Tool Technology',
  'Tool Life Monitoring']

},
{
  id: 2,
  x: 55,
  y: 50,
  title: 'Precision Work Table',
  description: 'High-speed positioning system with servo-driven axes for accurate hole placement and complex patterns.',
  specs: [
  '5000mm x 1250mm Work Area',
  'Servo-Driven X/Y Axes',
  'Positioning Speed: 100m/min',
  '±0.1mm Repeatability',
  'Automatic Sheet Clamping']

},
{
  id: 3,
  x: 75,
  y: 30,
  title: 'Control System',
  description: 'Advanced CNC control with integrated CAM software for optimized punching sequences and material efficiency.',
  specs: [
  'Fanuc CNC Control',
  'Integrated CAM Software',
  'Automatic Nesting',
  'Tool Path Optimization',
  'Production Monitoring']

}];


export default function InteractiveCNCPunching() {
  const [activeHotspot, setActiveHotspot] = useState<number | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  const handleHotspotClick = (id: number) => {
    if (!isHydrated) return;
    setActiveHotspot(activeHotspot === id ? null : id);
  };

  return (
    <section className="lg:py-24 bg-gradient-to-b from-muted/30 to-background py-[70px]">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-secondary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="CursorArrowRaysIcon" size={20} variant="solid" className="text-secondary" />
            <span className="text-sm font-cta font-semibold text-secondary">Interactive Showcase</span>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-6">
            CNC Punching Technology
          </h2>
          
          <p className="text-lg text-muted-foreground leading-relaxed">
            Discover our high-speed CNC punching capabilities with multi-tool turret technology for complex hole patterns and forming operations.
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <div className="relative bg-card rounded-xl shadow-lg overflow-hidden border border-border">
            <div className="relative aspect-video">
              <AppImage
                src="/assets/images/Sheet_Metal_IPunch-1770002355739.jpg"
                alt="Industrial CNC punching machine with multi-tool turret system processing metal sheet in modern manufacturing facility"
                className="w-full h-full object-cover" />

              
              {hotspots.map((hotspot) =>
              <button
                key={hotspot.id}
                onClick={() => handleHotspotClick(hotspot.id)}
                className="absolute w-12 h-12 -ml-6 -mt-6 group"
                style={{ left: `${hotspot.x}%`, top: `${hotspot.y}%` }}
                aria-label={`View details about ${hotspot.title}`}>

                  <div className="relative w-full h-full">
                    <div className="absolute inset-0 bg-secondary rounded-full animate-ping opacity-75"></div>
                    <div className="relative w-full h-full bg-secondary rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-base">
                      <Icon name="PlusIcon" size={24} variant="outline" className="text-white" />
                    </div>
                  </div>
                </button>
              )}
            </div>
            
            {isHydrated && activeHotspot !== null &&
            <div className="absolute bottom-0 left-0 right-0 bg-card/95 backdrop-blur-sm border-t border-border p-6 lg:p-8 animate-in slide-in-from-bottom duration-300">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl lg:text-2xl font-headline font-bold text-foreground mb-2">
                      {hotspots.find((h) => h.id === activeHotspot)?.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {hotspots.find((h) => h.id === activeHotspot)?.description}
                    </p>
                  </div>
                  <button
                  onClick={() => setActiveHotspot(null)}
                  className="p-2 hover:bg-muted rounded-md transition-colors duration-base"
                  aria-label="Close details">

                    <Icon name="XMarkIcon" size={24} variant="outline" className="text-muted-foreground" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {hotspots.find((h) => h.id === activeHotspot)?.specs.map((spec, index) =>
                <div key={index} className="flex items-center space-x-2 bg-muted/50 rounded-md px-3 py-2">
                      <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success flex-shrink-0" />
                      <span className="text-sm text-foreground">{spec}</span>
                    </div>
                )}
                </div>
              </div>
            }
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground flex items-center justify-center space-x-2">
              <Icon name="CursorArrowRippleIcon" size={20} variant="outline" />
              <span>Click the hotspots to explore CNC punching capabilities</span>
            </p>
          </div>
        </div>
      </div>
    </section>);

}